import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { Button, ButtonType } from "shared/components"

import { AnswerOptions } from "./useViewModel"

export type CheckboxFormProps = {
    answers: AnswerOptions
    onSubmit: (selected: string[]) => void
}

type CheckboxState = {
    selected: Record<string, boolean | undefined>
    noneOfTheAbove: boolean
    changed: boolean
}

const preventDefault =
    (fn: () => void) => (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        fn()
    }

export function CheckboxForm({ answers, onSubmit }: CheckboxFormProps) {
    const calculateInitialSelected = () => {
        if (answers.answered === "no") return {}
        return Object.fromEntries(
            answers.options.map((opt) => [opt.id, opt.state === "checked"]),
        )
    }

    const nullState = () => {
        const selected = calculateInitialSelected()
        const hasAnyChecked = Object.values(selected).some(Boolean)

        return {
            selected,
            noneOfTheAbove: !hasAnyChecked && answers.answered === "yes",
            changed: false,
        }
    }
    const [state, setState] = useState<CheckboxState>(nullState())

    // 🟢 Keep in sync if options change and there is no local state
    useEffect(() => {
        if (state.changed) return
        setState(nullState())
        // TODO: refactor so deps are exhaustive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [answers])

    const toggle = (id: string) => {
        setState((prev) => ({
            ...prev,
            selected: {
                ...prev.selected,
                [id]: !prev.selected[id],
            },
            changed: true,
        }))
    }

    const toggleNoneOfTheAbove = () => {
        setState((prev) => ({
            ...prev,
            noneOfTheAbove: !prev.noneOfTheAbove,
            changed: true,
        }))
    }

    const reset = () => {
        setState(nullState())
    }

    const handleSubmit = useCallback(() => {
        if (!state.changed) return

        if (state.noneOfTheAbove) {
            onSubmit([])
        } else {
            onSubmit(
                Object.entries(state.selected)
                    .filter(([_, checked]) => checked)
                    .map(([id]) => id),
            )
        }
        reset()
        // TODO: refactor so deps are exhaustive
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.changed, state.noneOfTheAbove, state.selected, onSubmit])
    const { t } = useTranslation()

    return (
        <form onSubmit={preventDefault(handleSubmit)} className="space-y-4">
            <fieldset className="space-y-2">
                <legend className="sr-only">{t("common.form.select_answers")}</legend>
                {answers.options.map((option) => {
                    const checked: boolean = state.selected[option.id] ?? false
                    const disabled: boolean = state.noneOfTheAbove

                    return (
                        <label
                            key={option.id}
                            className={`block select-none ${
                                disabled ? "opacity-50" : ""
                            }`}
                        >
                            <input
                                type="checkbox"
                                name="answer"
                                value={option.id}
                                checked={checked}
                                disabled={disabled}
                                onChange={() => toggle(option.id)}
                                className="mr-2"
                            />
                            {option.label}
                        </label>
                    )
                })}

                <label className="block cursor-pointer font-semibold select-none">
                    <input
                        type="checkbox"
                        name="noneOfTheAbove"
                        checked={state.noneOfTheAbove}
                        onChange={toggleNoneOfTheAbove}
                        className="mr-2"
                    />
                    {t("common.form.none_of_the_above")}
                </label>
            </fieldset>

            <Button
                buttonType={ButtonType.Primary}
                text={t("common.general.next")}
                enabled={state.changed}
                onClick={handleSubmit}
            />
        </form>
    )
}
